import * as Sentry from "@sentry/vue";
import { GRUPOS } from "./grupos";
import type { NomePapel } from "./vocabulario";

/**
 * Mapa de-para das 52 strings legadas de `roles[]` (PADRONIZACAO-ROLES.md §4)
 * para o nome canônico `<dominio>.<nivel|capacidade>`.
 *
 * `custom:roles` (Cognito) e o Dynamo continuam gravando os nomes antigos —
 * este mapa só traduz na fronteira de leitura (`normalizarPapeis`), sem exigir
 * migração de dado (§5.5).
 *
 * Quatro grupos de destino:
 * - **Renomeação pura** (§4.1, 36 entradas): um nome antigo, um nome novo.
 * - **Renomeação com upload** (§4.1, 3 entradas — `pos_vendas`,
 *   `editor_projetista`, `editor_projetista_fabrica`): o nível concedia
 *   upload junto da escrita; mapear para um papel só apagaria o upload em
 *   silêncio (§4.4 "Nível não implica capacidade"), então o alvo é um array
 *   de dois papéis.
 * - **Expansão de grupo** (§4.4/§4.5, 4 entradas — `site_manager`,
 *   `planejamento`, `pcp`, `mkt_read`): a string nomeia um GRUPO, e um grupo é
 *   um nome para uma lista de papéis. O alvo é a própria lista de `GRUPOS`,
 *   lida daqui e não copiada — é a T130, e é o que `catalogo/grupos.ts` chama
 *   de "ligação" no cabeçalho dele.
 * - **`null`** (6 entradas): a string não é papel — é `tipo` (`cliente`) ou
 *   escopo (`rdo_by_obra`), ou é uma das 4 ambíguas Gali×cliente.
 *
 * As 4 entradas ambíguas Gali×cliente (`projetista`, `projetista_fabrica`,
 * `aprovador_obra`, `aprovador_fabrica`, §4.2) mapeiam para `null`: o alvo
 * depende de `tipo` (dois significados por string, §2.4), e do lado cliente ele
 * é um GRUPO (`CLIENTE_LEITOR`/`CLIENTE_APROVADOR`, §4.2). `resolverPapeis` não
 * recebe `tipo`, então a expansão dessas quatro **não é** desta tarefa — é a
 * T129, que resolve por `tipo` e não por nome. Ver relatório de T44 e AD-032.
 */
export const LEGADO = {
  // --- §4.1: renomeação pura ---
  admin_automacao: "automacao.admin",
  automacao_config: "automacao.config",
  automacao: "automacao.escrita",
  admin_biblioteca: "biblioteca.admin",
  admin_contratos: "contratos.admin",
  editor_contratos: "contratos.escrita",
  contratos: "contratos.leitura",
  admin_fabricacao: "fabricacao.admin",
  fabricacao: "fabricacao.escrita",
  admin_field: "field.admin",
  field_config: "field.config",
  field: "field.escrita",
  admin_juridico: "juridico.admin",
  leitura_juridico: "juridico.leitura",
  admin_materiais: "materiais.admin",
  editor_materiais: "materiais.escrita",
  // MAT-01 (conferência de acessos): quem tem `materiais` também cria material de obra e de projeto.
  materiais: ["materiais.leitura", "materiais.criacao"],
  admin_obra: "obra.admin",
  obra: "obra.escrita",
  admin_pos_vendas: "posvendas.admin",
  pos_vendas_leitura: "posvendas.leitura",
  // PRD-01 (conferência de acessos): o menu "Produto" abre TAF, Checklist e
  // Instrumentos, que exigem o recurso `TAF` — os três papéis levam o nível de TAF.
  admin_produtos: ["produto.admin", "taf.admin"],
  editor_produto: ["produto.escrita", "taf.escrita"],
  produto: ["produto.leitura", "taf.leitura"],
  admin_taf: "taf.admin",
  editor_taf: "taf.escrita",
  taf: "taf.leitura",
  admin_projetos: "projetos.admin",
  admin_projetos_fabrica: "projetos_fabrica.admin",
  admin_rdo: "rdo.admin",
  admin_seguros: "seguros.admin",
  seguros: "seguros.escrita",
  admin_sgi: "sgi.admin",
  sgi: "sgi.escrita",
  sgi_leitura: "sgi.leitura",
  acesso_total: "acesso.total",

  // --- §4.1: renomeação com upload (nível não implica capacidade, §4.4) ---
  pos_vendas: ["posvendas.escrita", "posvendas.upload"],
  editor_projetista: ["projetos.escrita", "projetos.upload"],
  editor_projetista_fabrica: ["projetos_fabrica.escrita", "projetos_fabrica.upload"],

  // --- §4.4: sai de roles[] — vira tipo, grupo, escopo ou flag de ambiente ---
  cliente: null, // vira `tipo: "cliente"` — T48
  // So vale para `tipo: "cliente"` (no original estava dentro do ramo cliente de
  // `cliente.ts`): alvo `null` aqui, e a traducao por `tipo` mora em `AMBIGUAS_CLIENTE`
  // (`shared/autorizacao/index.ts`) — mesmo mecanismo das quatro strings de §4.2, so
  // que sem lado Gali. Port de origin/permissao-materias-para-cliente fde84ffc.
  materiais_cliente: null,
  // T130: as quatro strings de grupo SEM ambiguidade de `tipo` deixam de ser
  // "nao e papel ainda" e passam a expandir na lista de papeis do grupo. O alvo e
  // `GRUPOS` lido daqui: copiar a lista faria dela a segunda copia do mesmo fato.
  //
  // AMPLIACAO ACEITA (AD-034 D3, textual do usuario: "ok"). `GRUPOS.SITE_MANAGER`
  // carrega `field.escrita` e `automacao.escrita`, que hoje exigem as roles `field`
  // e `automacao`: expandir da ao site manager 14 concessoes que ele nao tem hoje.
  // O grupo NAO encolhe; a ampliacao vai a lista de avisos a area. As divergencias
  // exatas, nos dois sentidos, estao em `DIVERGENCIAS_DECIDIDAS` do teste de
  // equivalencia (T55) — que e quem as mede contra o agregador imperativo.
  site_manager: GRUPOS.SITE_MANAGER,
  planejamento: GRUPOS.PLANEJAMENTO,
  pcp: GRUPOS.PCP,
  mkt_read: GRUPOS.MKT,
  // NAO apagado em T50, de proposito: `rdo.ts:10` concede `read` em RDO condicionado
  // as obras do usuario, e hoje NADA concede leitura ampla de RDO a um Gali sem
  // `admin_rdo`. Deletar agora TIRA acesso em vez de ampliar. Some quando
  // `PAPEIS_RDO` conceder `rdo.leitura` — T52/T53.
  rdo_by_obra: null,
  // Decisao do usuario em 2026-09-05, com a escalacao explicitada antes: "pode
  // mapear para acesso.total". O gate de AMBIENTE sai do codigo — quem detem
  // `acesso.total` administra, e em que ambiente cada conta o detem passa a ser
  // decisao de operacao, nao condicao compilada. Consequencia aceita: quem hoje so
  // tem manage-all em dev passa a ter em producao. Ver AD-026.
  dev_acesso_total: "acesso.total",

  // --- §4.2: a condição fica no registro, não no nome do papel (§7b) ---
  // T49 resolveu o §7b: a origem virou RECURSO ("MateriaisObra"/"MateriaisProjeto"),
  // nao sufixo no nome da acao. A T56 fechou o resto, com a decisao D2 do AD-031:
  // uma capacidade so, `materiais.criacao`, concedendo `create` nos DOIS recursos.
  //
  // A ampliacao e o preco, e esta aceita textualmente pelo usuario: quem hoje detem
  // so `materiais_obra` passa a criar por projeto tambem. Quantas pessoas isso
  // atinge e dado de producao, nao do repositorio.
  //
  // Nao e nenhuma das duas saidas que o §7b listou (capacidade unica com condicao,
  // ou um nome de capacidade por origem — este ultimo proibido la). E a terceira:
  // uma capacidade, dois recursos.
  // T33 (adequação de Materiais, MAT-17): `materiais_obra` também registra o recebimento
  // na obra — o `editarObra` do `cbb62e14`, decisão do usuário.
  materiais_obra: ["materiais.criacao", "materiais.recebimento"],
  materiais_projeto: "materiais.criacao",

  // --- §4.2: ambíguo Gali×cliente, resolução depende de `tipo` (§2.4) — achado, não corrigido aqui ---
  // Do lado cliente as quatro apontam para os grupos `CLIENTE_LEITOR` e
  // `CLIENTE_APROVADOR` (§4.2), e do lado Gali para papeis de `projetos`/
  // `projetos_fabrica`. A T130 nao as expande porque `resolverPapeis` nao recebe
  // `tipo` — e a T129 que resolve por `tipo`, e nao por nome.
  projetista: null,
  projetista_fabrica: null,
  aprovador_obra: null,
  aprovador_fabrica: null,
} as const satisfies Record<string, NomePapel | readonly NomePapel[] | null>;

/** As chaves de `LEGADO` cujo alvo é `null` — ainda não viram `NomePapel`. */
type ChavesNulas<T> = { [K in keyof T]: T[K] extends null ? K : never }[keyof T];

/**
 * Strings legadas que `temPermissao` (T46) ainda aceita cruas, sem tradução
 * para `NomePapel` — cada uma com a tarefa que a remove, documentada em
 * `LEGADO` acima.
 *
 * **Encolheu de 10 chaves para 6 na T130**, e o mecanismo é o ponto: as quatro
 * strings de grupo (`site_manager`, `planejamento`, `pcp`, `mkt_read`) saíram do
 * conjunto de nulas porque passaram a ter alvo, não porque alguém editou este
 * tipo. O que sobra é `cliente` (é `tipo`, não papel), `rdo_by_obra` (é escopo) e
 * as 4 ambíguas Gali×cliente, que a T129 resolve por `tipo`.
 */
export type PapelTransicao = ChavesNulas<typeof LEGADO>;

/**
 * TODAS as 52 chaves de `LEGADO` — o vocabulário que `temPermissao` (T46) aceita
 * além dos nomes canônicos.
 *
 * `PapelTransicao` sozinha não serve para tipar a assinatura, e a medição mostra
 * por quê: ela cobre só as 6 chaves nulas, mas o kernel de autorização passa ~63
 * literais que são chaves NÃO-nulas (`admin_obra`, `editor_contratos`, `sgi_leitura`
 * e companhia). Tipar com `NomePapel | PapelTransicao` reprovaria o kernel inteiro.
 *
 * O conjunto é deliberadamente amplo agora e **encolhe sozinho**: cada tarefa que
 * apaga um nome legado o remove de `LEGADO`, e a assinatura fica mais estrita no
 * mesmo commit, sem ninguém precisar lembrar. O que ele já impede hoje é o que T46
 * vende — papel inventado na chamada não compila.
 */
export type PapelLegado = keyof typeof LEGADO;

/**
 * Traduz papéis brutos (de `custom:roles`/Dynamo) para o nome canônico.
 *
 * Papel que não está em `LEGADO` nem já é um `NomePapel` (ex.: digitado
 * errado, ou removido do catálogo) é descartado e reportado com nome e
 * e-mail — em vez de sumir em silêncio ou derrubar a aplicação.
 */
export function normalizarPapeis(brutos: string[], email?: string): NomePapel[] {
  const { traduzidos, desconhecidos } = resolverPapeis(brutos);

  if (desconhecidos.length) {
    Sentry.captureMessage("papel_desconhecido", { extra: { desconhecidos, email } });
  }

  return traduzidos;
}

/**
 * Tradução pura: os mesmos papéis, **sem** o efeito colateral do Sentry.
 *
 * Existe separada porque as regras de autorização precisam resolver papel a cada
 * `defineAbilityFor`, e reportar ao Sentry a cada build de ability transformaria um
 * aviso útil em ruído. `normalizarPapeis` continua sendo o ponto de fronteira, que
 * roda uma vez por login e é quem reporta.
 *
 * Aceita nome legado e nome já canônico: um papel que não está em `LEGADO` mas
 * obedece à gramática passa direto, o que é o que acontece quando `custom:roles` e o
 * Dynamo migrarem.
 */
export function resolverPapeis(brutos: string[]): {
  traduzidos: NomePapel[];
  desconhecidos: string[];
} {
  const traduzidos: NomePapel[] = [];
  const desconhecidos: string[] = [];

  for (const bruto of brutos) {
    if (Object.prototype.hasOwnProperty.call(LEGADO, bruto)) {
      const alvo = LEGADO[bruto as keyof typeof LEGADO];
      if (alvo === null) continue;
      if (Array.isArray(alvo)) traduzidos.push(...(alvo as readonly NomePapel[]));
      else traduzidos.push(alvo as NomePapel);
    } else if (/^[a-z][a-z_]*\.[a-z][a-z_]*$/.test(bruto)) {
      traduzidos.push(bruto as NomePapel);
    } else {
      desconhecidos.push(bruto);
    }
  }

  return { traduzidos, desconhecidos };
}

/** Se o usuário detém o papel canônico pedido, venha ele por nome legado ou já canônico. */
export function detemPapel(brutos: string[], papel: NomePapel): boolean {
  return resolverPapeis(brutos).traduzidos.includes(papel);
}
